import { useEffect, useState } from 'react';
import { apiClient } from '../auth-lib';

// The live figures on the sidebar's pills: open tasks, filings more than a week
// past deadline, open to-dos, pending approvals.
//
// ⚠️ MODULE-LEVEL CACHE, FOR THE REASON `tallyNavStatus.js` HAS ONE: every page
// renders its own `<OrgAdminLayout>`, so the layout remounts on every
// navigation and a plain `useEffect` fetch would hit the server on each click.
//
// ⚠️ A SHORTER TTL THAN THE TALLY DOT'S. These numbers move when the user
// themselves closes a task or approves a proposal, and a badge that still says
// 29 a minute after you cleared one reads as broken. 20s, plus an explicit
// `invalidateNavCounts()` for the screens that change them.
const TTL_MS = 20_000;

let cache = null; // { at, value }
let inFlight = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(cache?.value ?? null));

/** Drop the cache — call after anything that changes one of these four. */
export function invalidateNavCounts() {
  cache = null;
  inFlight = null;
  // ⚠️ REFETCH, DO NOT MERELY FORGET. Dropping the cache alone corrects the
  // pills on the NEXT navigation — but the screen that just changed the number
  // is the one the user is standing on, and they have no reason to navigate.
  // The stale figure is exactly what this was meant to prevent.
  //
  // ⚠️ ONLY WHEN SOMETHING IS LISTENING. With no sidebar mounted there is
  // nothing to update, and a request per write from a headless caller is pure
  // waste.
  if (listeners.size) fetchCounts();
}

function fetchCounts() {
  // A navigation can mount the layout while the previous request is open;
  // without this, clicking quickly through pages opens one request per click.
  if (inFlight) return inFlight;

  inFlight = apiClient
    .get('/api/nav-counts')
    .then((res) => {
      cache = { at: Date.now(), value: res.data || {} };
      notify();
      return cache.value;
    })
    .catch(() => {
      // ⚠️ CACHED AS EMPTY, NOT RETRIED IN A LOOP. The pills simply do not
      // render — which is the same as this sidebar looked before it had them —
      // rather than turning one failure into a request per navigation for ever.
      cache = { at: Date.now(), value: {} };
      notify();
      return cache.value;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function useNavCounts(enabled = true) {
  const [value, setValue] = useState(() => cache?.value ?? null);

  useEffect(() => {
    if (!enabled) return undefined;

    listeners.add(setValue);
    const fresh = cache && Date.now() - cache.at < TTL_MS;
    if (fresh) setValue(cache.value);
    else fetchCounts();

    return () => listeners.delete(setValue);
  }, [enabled]);

  return value;
}
